/**
 * Modal scanner OCR de date de péremption (anti-confusion FAB/PER).
 *
 * Stratégie multi-couches :
 * 1. Capture toutes les dates du texte OCR avec leur contexte (30 chars avant)
 * 2. Classifie chaque date selon les mots-clés trouvés :
 *    - PER/EXP/PÉR/EXPIRY/USE BY → péremption
 *    - FAB/MFD/MFG/PROD          → fabrication (REJET)
 *    - aucun mot-clé             → inconnu
 * 3. Sanity check temporel :
 *    - date passée ou <1 mois  → suspect (probablement FAB)
 *    - date >10 ans futur      → trop loin (suspect)
 * 4. Pick automatique : 1ère péremption plausible. Sinon agent choisit.
 *
 * Props :
 * - onDetect(isoDate: string|null, rawText: string)
 * - onClose()
 */
import React, { useRef, useState } from 'react';
import { X, Camera, Loader2, AlertCircle, Check, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { preprocessLabel } from '../../../utils/imagePreprocess';

const PEREMPTION_KW = [
  'EXP', 'PER', 'PÉR', 'PERE', 'PÉRE', 'EXPIRY', 'EXPIRES', 'EXPIRE',
  'USE BY', 'USEBY', 'BEST BEFORE', 'BB', 'EOL',
  'À UTILISER AVANT', 'A UTILISER AVANT', 'À CONSOMMER AVANT',
  'CADUCIDAD', 'CAD', 'VENC', 'VAL', 'VALIDADE', 'VTO', 'VENCE',
];
const FABRICATION_KW = [
  'FAB', 'MFD', 'MFG', 'PROD', 'PRODUCED',
  'FABRICATION', 'FABRIQUÉ', 'FABRIQUE',
  'MANUF', 'MANUFACTURED', 'MADE', 'DOM', 'DOP',
];

// Cherche le mot-clé le plus proche (= le plus à droite) du début de la date.
// Retourne {kw, pos} ou {kw: null, pos: -1}.
const findClosestKw = (before, keywords) => {
  let bestKw = null;
  let bestPos = -1;
  for (const kw of keywords) {
    const p = before.lastIndexOf(kw);
    if (p > bestPos) { bestPos = p; bestKw = kw; }
  }
  return { kw: bestKw, pos: bestPos };
};

// Cherche toutes les dates dans `text` avec leur contexte (≤30 chars avant).
// Retourne [{iso, raw, context, kind}].
const extractDatesWithContext = (text) => {
  if (!text) return [];
  // OCR confond souvent O↔0 et I/l↔1. On normalise uniquement quand entouré de chiffres.
  const cleaned = text
    .replace(/([\dO])O([\dO])/g, '$10$2')
    .replace(/([\dO])O([\dO])/g, '$10$2')
    .replace(/(\d)[Il](\d)/g, '$11$2');

  const found = [];
  const seen = new Set();

  const pushIfPlausible = (idx, iso, raw) => {
    if (!iso || seen.has(iso + '@' + idx)) return;
    seen.add(iso + '@' + idx);
    const before = cleaned.slice(Math.max(0, idx - 30), idx).toUpperCase();
    const after = cleaned.slice(idx + raw.length, idx + raw.length + 4).toUpperCase();
    const ctx = before + ' [' + raw + '] ' + after;
    // Le mot-clé le plus proche de la date gagne (évite les confusions
    // multi-keywords du type "VAL: 12/2028\nFAB: 12/2024" où la 2e date
    // doit être classée fabrication malgré la présence de VAL en amont).
    const fab = findClosestKw(before, FABRICATION_KW);
    const exp = findClosestKw(before, PEREMPTION_KW);
    let kind = 'unknown';
    if (fab.pos === -1 && exp.pos === -1) kind = 'unknown';
    else if (fab.pos > exp.pos) kind = 'fabrication';
    else kind = 'peremption';
    found.push({ iso, raw, context: ctx, kind, idx });
  };

  // Format JJ/MM/AAAA
  const fullRe = /\b(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})\b/g;
  let m;
  while ((m = fullRe.exec(cleaned)) !== null) {
    let [, dd, mm, yyyy] = m;
    if (yyyy.length === 2) yyyy = (parseInt(yyyy, 10) >= 70 ? '19' : '20') + yyyy;
    const d = +dd, mo = +mm, y = +yyyy;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && y >= 2020 && y <= 2099) {
      pushIfPlausible(m.index, `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`, m[0]);
    }
  }

  // Format MM/AAAA ou MM-AAAA (sans jour, courant sur les médicaments)
  const myRe = /\b(\d{1,2})[\/\-.](\d{4})\b/g;
  while ((m = myRe.exec(cleaned)) !== null) {
    const mo = +m[1], y = +m[2];
    if (mo >= 1 && mo <= 12 && y >= 2020 && y <= 2099) {
      // Évite de rejouer un match déjà capturé en plein milieu d'une JJ/MM/AAAA
      const overlapping = found.some(f => m.index >= f.idx && m.index < f.idx + f.raw.length);
      if (!overlapping) pushIfPlausible(m.index, `${y}-${String(mo).padStart(2, '0')}-01`, m[0]);
    }
  }

  // Format MMM AAAA (ex: JAN 2030)
  const monthsAbbr = { JAN: 1, FEV: 2, FEB: 2, MAR: 3, AVR: 4, APR: 4, MAI: 5, MAY: 5, JUN: 6, JUI: 7, JUL: 7, AOU: 8, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };
  const txtUp = cleaned.toUpperCase();
  for (const [abbr, mo] of Object.entries(monthsAbbr)) {
    const re = new RegExp(`\\b${abbr}[A-Z]*[\\s\\-]+(\\d{4})\\b`, 'g');
    while ((m = re.exec(txtUp)) !== null) {
      const y = parseInt(m[1], 10);
      if (y >= 2020 && y <= 2099) {
        pushIfPlausible(m.index, `${y}-${String(mo).padStart(2, '0')}-01`, m[0]);
      }
    }
  }

  return found;
};

// Ajoute un drapeau temporel : past / soon / plausible / too_far
const annotatePlausibility = (results) => {
  const today = new Date();
  return results.map(r => {
    const [y, mo, d] = r.iso.split('-').map(Number);
    const dt = new Date(y, mo - 1, d);
    const diffMs = dt - today;
    const days = diffMs / (1000 * 60 * 60 * 24);
    let plausible = 'plausible';
    if (days < -30) plausible = 'past';
    else if (days < 30) plausible = 'soon';
    else if (days > 365 * 10) plausible = 'too_far';
    return { ...r, plausible, days: Math.round(days) };
  });
};

// Choisit la meilleure date péremption candidate
const pickBestPeremption = (results) => {
  const peremption = results.filter(r => r.kind === 'peremption' && r.plausible !== 'past');
  if (peremption.length) {
    peremption.sort((a, b) => b.iso.localeCompare(a.iso));
    return { best: peremption[0], reason: 'mot_cle_per' };
  }
  // Pas de PER explicite : on rejette tout ce qui est FAB et on prend la date la plus future plausible
  const candidates = results.filter(r => r.kind !== 'fabrication' && r.plausible === 'plausible');
  if (candidates.length) {
    candidates.sort((a, b) => b.iso.localeCompare(a.iso));
    return { best: candidates[0], reason: 'plus_future' };
  }
  return { best: null, reason: 'aucune' };
};

const KIND_BADGE = {
  peremption: { label: 'Péremption ✓', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  fabrication: { label: 'Fabrication ✗', cls: 'bg-red-100 text-red-700 border-red-300' },
  unknown: { label: 'Sans étiquette', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
};
const PLAUSIBLE_BADGE = {
  past: { label: 'Déjà passée', cls: 'bg-red-50 text-red-700' },
  soon: { label: '<30 j (suspect)', cls: 'bg-amber-50 text-amber-700' },
  plausible: { label: 'Plausible', cls: 'bg-emerald-50 text-emerald-700' },
  too_far: { label: '>10 ans (suspect)', cls: 'bg-amber-50 text-amber-700' },
};

const formatHumanDelay = (days) => {
  if (days < 0) return `il y a ${Math.abs(days)} j`;
  if (days < 30) return `dans ${days} j`;
  const months = Math.round(days / 30);
  if (months < 24) return `dans ${months} mois`;
  const years = Math.floor(months / 12);
  const remM = months % 12;
  return remM ? `dans ${years} an${years > 1 ? 's' : ''} ${remM} mois` : `dans ${years} an${years > 1 ? 's' : ''}`;
};

// ───────────────────────────────────────────────────────────
// Pré-traitement image — voir /app/frontend/src/utils/imagePreprocess.js
// ───────────────────────────────────────────────────────────
// `preprocessLabel` (preset partagé) :
//   - 2000px max + EXIF auto-orient + niveaux de gris + contraste 30 + sharpening 3x3
//   - Normalise iPhone HEIC/48Mpx et améliore drastiquement la précision Tesseract.

const ExpirationOcrDialog = ({ onDetect, onClose }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rawText, setRawText] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState(null);   // iso choisi par l'utilisateur ou auto
  const [pickReason, setPickReason] = useState(null);
  const [error, setError] = useState(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setCandidates([]);
    setPicked(null);
    setPickReason(null);
    setRawText('');
    setBusy(true);
    try {
      // 1. Prétraitement : 2000px + EXIF + niveaux de gris + contraste fort + sharpening 3x3
      // (corrige l'incompatibilité iOS HEIC, photos 48 Mpx, orientation EXIF, et améliore
      //  la lisibilité des petites dates EXP/PER imprimées en bas d'étiquette)
      let imageSource = file;
      try {
        const { blob, dataUrl } = await preprocessLabel(file);
        imageSource = blob;
        setPreview(dataUrl);
      } catch (preErr) {
        // Fallback : on tente l'OCR sur le fichier brut si la conversion échoue
        // (image non décodable par le navigateur — rare).
        setPreview(URL.createObjectURL(file));
        // eslint-disable-next-line no-console
        console.warn('Pré-traitement image échoué, OCR sur fichier brut :', preErr);
      }
      // 2. OCR. Eng+digits suffit pour les dates même sur étiquettes FR/PT/ES.
      const Tesseract = (await import('tesseract.js')).default;
      const { data } = await Tesseract.recognize(imageSource, 'eng');
      const text = (data?.text || '').trim();
      setRawText(text);
      const results = annotatePlausibility(extractDatesWithContext(text));
      setCandidates(results);
      const { best, reason } = pickBestPeremption(results);
      setPicked(best?.iso || null);
      setPickReason(reason);
    } catch (err) {
      setError(err?.message || 'Erreur OCR');
    } finally {
      setBusy(false);
    }
  };

  const validate = () => {
    onDetect(picked, rawText);
  };

  const reset = () => {
    setPreview(null); setRawText(''); setCandidates([]); setPicked(null); setPickReason(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="ocr-date-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#5B3B8C] to-[#7C4FB8] px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <h2 className="font-semibold">Scanner date de péremption</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="ocr-date-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3">
          {!preview && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full aspect-[4/3] border-2 border-dashed border-purple-300 rounded-lg bg-purple-50 hover:bg-purple-100 flex flex-col items-center justify-center gap-2 text-[#5B3B8C] transition"
                data-testid="ocr-date-capture-btn"
              >
                <Camera className="w-10 h-10" />
                <span className="text-sm font-medium">Prendre une photo</span>
                <span className="text-xs text-zinc-500 px-3 text-center leading-relaxed">
                  Cadrez la date <b>EXP</b> ou <b>PÉR</b><br/>
                  Approchez-vous (date occupant ⅓ de la photo)<br/>
                  Évitez les reflets et l'ombre sur la date
                </span>
              </button>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">⚠️ Ne pas confondre :</p>
                  <p className="mt-0.5"><b>EXP</b> / <b>PER</b> / <b>PÉR</b> / <b>CAD</b> / <b>VAL</b> = péremption (à scanner ✅)</p>
                  <p><b>FAB</b> / <b>MFD</b> / <b>MFG</b> = fabrication (à éviter ❌)</p>
                  <p className="mt-1.5 text-[11px] text-amber-700">
                    💡 <b>iPhone</b> : si l'OCR échoue, désactivez HEIC dans Réglages → Appareil photo → Formats → <i>Le plus compatible</i>.
                  </p>
                </div>
              </div>
            </>
          )}
          {preview && (
            <img src={preview} alt="capture" className="w-full max-h-52 object-contain rounded-md bg-zinc-100" />
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleCapture}
            data-testid="ocr-date-file-input"
          />

          {busy && (
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyse OCR en cours…
            </div>
          )}

          {!busy && preview && (
            <div className="space-y-3">
              {candidates.length === 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <span>Aucune date détectée. Reprenez la photo ou saisissez manuellement.</span>
                </div>
              )}

              {candidates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">
                    {candidates.length} date{candidates.length > 1 ? 's' : ''} détectée{candidates.length > 1 ? 's' : ''}
                  </p>
                  {candidates.map((c, i) => {
                    const isPicked = picked === c.iso;
                    const kindB = KIND_BADGE[c.kind];
                    const plB = PLAUSIBLE_BADGE[c.plausible];
                    const disabled = c.kind === 'fabrication' || c.plausible === 'past';
                    return (
                      <button
                        key={i}
                        onClick={() => !disabled && setPicked(c.iso)}
                        disabled={disabled}
                        className={`w-full text-left rounded-lg border p-2.5 transition ${
                          isPicked
                            ? 'border-[#5B3B8C] bg-purple-50 ring-2 ring-[#5B3B8C]/20'
                            : disabled
                              ? 'border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed'
                              : 'border-zinc-200 hover:border-purple-300 hover:bg-purple-50/50 cursor-pointer'
                        }`}
                        data-testid={`ocr-candidate-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-mono font-bold text-zinc-900">
                            {c.iso} <span className="text-xs text-zinc-500 font-normal">({c.raw})</span>
                          </span>
                          {isPicked && <Check className="w-4 h-4 text-[#5B3B8C]" />}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${kindB.cls}`}>
                            {kindB.label}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${plB.cls}`}>
                            {plB.label}
                          </span>
                          <span className="text-[10px] text-zinc-500">
                            {formatHumanDelay(c.days)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {picked && pickReason === 'mot_cle_per' && (
                <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 p-2 rounded text-xs">
                  <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Auto-sélection grâce au mot-clé <b>EXP/PER</b> trouvé sur la boîte.</span>
                </div>
              )}
              {picked && pickReason === 'plus_future' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Aucun mot-clé <b>EXP/PER</b> trouvé. La date la plus future a été choisie par défaut.
                    <b> Vérifiez sur la boîte avant de valider.</b>
                  </span>
                </div>
              )}

              {rawText && (
                <details className="text-xs text-zinc-500">
                  <summary className="cursor-pointer">Texte OCR brut</summary>
                  <pre className="bg-zinc-50 p-2 rounded mt-1 whitespace-pre-wrap break-words">{rawText.slice(0, 400)}</pre>
                </details>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} className="flex-1" data-testid="ocr-date-retry">
                  Reprendre
                </Button>
                <Button
                  onClick={validate}
                  disabled={!picked}
                  className="flex-1 bg-[#5B3B8C] hover:bg-[#4A2F73] text-white disabled:opacity-50"
                  data-testid="ocr-date-validate"
                >
                  Valider {picked ? `${picked}` : ''}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpirationOcrDialog;
